/**
 * ComposioConnectCard — the interactive card the chat shows when the agent
 * calls the client-side `connect_composio_integration` tool.
 *
 * It embeds the existing ProfileConnector (the same OAuth-popup + status-poll
 * flow used in the agent builder) inline in the composer slot. When the user
 * finishes connecting, `onProfileConnected` fires and we resolve the suspended
 * tool call via the parent's `onConnected`; closing/skipping resolves it as
 * declined so the agent never hangs.
 */

import { useMemo } from "react";
import { X } from "lucide-react";

import { Button, Card } from "@/components/ui";
import { ProfileConnector } from "@/components/agents/builder/integrations";
import type {
  ComposioProfile,
  IntegrationApp,
} from "@/components/agents/builder/integrations/types";

export interface ComposioConnectInput {
  toolkitSlug: string;
  displayName?: string;
  reason?: string;
}

export interface ComposioConnectResult {
  connected: true;
  toolkitSlug: string;
  profileId: string;
  profileName?: string;
}

export interface PendingComposioConnect {
  toolCallId: string;
  input: ComposioConnectInput;
}

interface ComposioConnectCardProps {
  pending: PendingComposioConnect;
  onConnected: (toolCallId: string, result: ComposioConnectResult) => void;
  onDismiss: (toolCallId: string) => void;
  disabled?: boolean;
}

export function ComposioConnectCard({
  pending,
  onConnected,
  onDismiss,
  disabled,
}: ComposioConnectCardProps) {
  const { toolCallId, input } = pending;
  const displayName = input.displayName?.trim() || input.toolkitSlug;

  // ProfileConnector fetches full toolkit details from `app.slug` on mount, so
  // a minimal IntegrationApp is enough to bootstrap the connect flow.
  const app = useMemo<IntegrationApp>(
    () => ({
      slug: input.toolkitSlug,
      name: displayName,
      description: input.reason ?? "",
      logo: "",
      authSchemes: [],
      categories: [],
      type: "composio",
    }),
    [input.toolkitSlug, displayName, input.reason],
  );

  return (
    <Card
      className="w-full overflow-hidden border-primary/30"
      data-testid="composio-connect-card"
    >
      <div className="flex items-start justify-between gap-3 border-b bg-muted/30 px-4 py-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">
            Connect {displayName}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {input.reason?.trim() ||
              `Authorize ${displayName} so the agent can use it.`}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7 shrink-0"
          aria-label="Skip connecting"
          data-testid="composio-connect-dismiss"
          onClick={() => onDismiss(toolCallId)}
          disabled={disabled}
        >
          <X className="size-4" />
        </Button>
      </div>

      <div className="max-h-[460px] overflow-y-auto">
        <ProfileConnector
          app={app}
          open
          isEmbedded
          showHeader={false}
          showToolsPreview={false}
          onOpenChange={(open) => {
            if (!open) onDismiss(toolCallId);
          }}
          onProfileConnected={(profile: ComposioProfile) => {
            onConnected(toolCallId, {
              connected: true,
              toolkitSlug: profile.toolkitSlug || input.toolkitSlug,
              profileId: profile.profileId,
              profileName: profile.profileName,
            });
          }}
        />
      </div>
    </Card>
  );
}
